import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { awsService, ResourceSummary } from '../../services/awsService';
import { billingService, MonthlyCostData } from '../../services/billingService';
import { authService } from '../../services/authService';
import ResourceSummaryCards from './ResourceSummaryCards';
import SyncPanel from './SyncPanel';
import BandwidthDashboard from './BandwidthDashboard';
import AWSRepatriationPlan from './AWSRepatriationPlan';
import ServiceDetailsView from './ServiceDetailsView';
import ResourcePieCharts from './ResourcePieCharts';
import Toast, { ToastType } from '../Toast';
import Logo from '../Logo';
import DebugPanel, { DebugValues } from './DebugPanel';
import { 
  ArrowPathIcon, 
  ExclamationTriangleIcon,
  ArrowDownTrayIcon,
  CloudArrowDownIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon 
} from '@heroicons/react/24/outline';

const Dashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const [summary, setSummary] = useState<ResourceSummary | null>(null);
  const [billingData, setBillingData] = useState<MonthlyCostData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState('');
  const [syncError, setSyncError] = useState('');
  const [error, setError] = useState('');
  const [showSyncPanel, setShowSyncPanel] = useState(false);
  const [bandwidthTotalGB, setBandwidthTotalGB] = useState<number>(0);
  const [bandwidthCost, setBandwidthCost] = useState<number>(0);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const [debugPanelOpen, setDebugPanelOpen] = useState(false);
  const [debugValues, setDebugValues] = useState<DebugValues | null>(null);
  const [showRepatriationPlan, setShowRepatriationPlan] = useState(true);

  useEffect(() => {
    loadDashboardData();
    loadUserInviteCodeInfo();
  }, []);

  const loadDashboardData = async () => {
    try {
      setError('');
      
      // Load resource summary
      const summaryData = await awsService.getResourceSummary();
      setSummary(summaryData);
      
      // Try to load billing data (don't fail if it errors)
      try {
        console.log('Fetching billing data...');
        const billing = await billingService.getMonthlyCosts();
        console.log('Billing data received:', billing);
        setBillingData(billing);
      } catch (billingErr: any) {
        console.error('Could not load billing data:', billingErr.response?.data?.error || billingErr.message);
        // Don't set error state - billing is optional
      }
    } catch (err: any) {
      console.error('Dashboard data loading error:', err);
      setError(err.response?.data?.error || 'Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  };

  const loadUserInviteCodeInfo = async () => {
    try {
      const inviteInfo = await authService.getUserInviteCodeInfo();
      setShowRepatriationPlan(inviteInfo.showRepatriationPlan);
    } catch (error) {
      console.error('Error loading invite code info:', error);
      // Default to not showing repatriation plan if error
      setShowRepatriationPlan(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await loadDashboardData();
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      // Get the auth token
      const token = localStorage.getItem('authToken');
      if (!token) {
        setError('Authentication required');
        return;
      }

      // Show loading toast
      setToast({ message: 'Generating Excel export...', type: 'info' });

      // Create a download link
      const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/aws/export/excel`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to export data');
      }

      // Get the filename from content-disposition header
      const contentDisposition = response.headers.get('content-disposition');
      const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
      const filename = filenameMatch ? filenameMatch[1] : 'aws-resources-export.xlsx';

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      setToast({ message: 'Excel export completed successfully!', type: 'success' });
    } catch (error: any) {
      console.error('Export error:', error);
      setError('Failed to export Excel file');
      setToast({ message: 'Failed to export Excel file', type: 'error' });
    }
  };

  const handleSyncAllRegions = async (useUnifiedSync: boolean = false) => {
    setIsSyncing(true);
    setSyncError('');
    setSyncProgress('Initializing sync...');
    setToast({ message: useUnifiedSync ? 'Starting unified sync (resources + bandwidth)...' : 'Starting resource sync...', type: 'info' });
    
    try {
      // Get all active credentials
      const { credentials } = await awsService.getCredentials();
      const activeCredentials = credentials.filter(cred => cred.isActive);
      
      if (activeCredentials.length === 0) {
        setToast({ message: 'No active AWS credentials found. Please add credentials first.', type: 'error' });
        setIsSyncing(false);
        setSyncProgress('');
        return;
      }

      let completedSyncs = 0;
      const totalSyncs = activeCredentials.length;
      
      // Define regions for unified sync
      const regions = ['us-east-1', 'us-east-2', 'us-west-1', 'us-west-2',
                      'eu-west-1', 'eu-west-2', 'eu-central-1',
                      'ap-southeast-1', 'ap-southeast-2', 'ap-northeast-1'];

      // Sync each credential
      for (const credential of activeCredentials) {
        try {
          setSyncProgress(`Syncing ${credential.credentialName}... (${completedSyncs + 1}/${totalSyncs})`);
          
          if (useUnifiedSync) {
            // Use unified sync endpoint
            await awsService.unifiedSync(credential.id, regions);
          } else {
            // Use regular resource sync
            await awsService.syncAllResources(credential.id);
          }
          
          completedSyncs++;
        } catch (error: any) {
          console.error(`Failed to sync ${credential.credentialName}:`, error);
          // Continue with other credentials even if one fails
        }
      }

      setSyncProgress('Refreshing dashboard...');
      
      // Refresh dashboard data
      await loadDashboardData();
      
      if (completedSyncs === totalSyncs) {
        setSyncProgress('');
        setToast({ message: `${useUnifiedSync ? 'Unified sync' : 'Resource sync'} completed successfully! Updated ${completedSyncs} AWS account${completedSyncs > 1 ? 's' : ''}.`, type: 'success' });
        
        // Auto-refresh page for unified sync
        if (useUnifiedSync) {
          setTimeout(() => {
            window.location.reload();
          }, 2000);
        }
      } else {
        setSyncProgress('');
        setToast({ message: `Sync completed with some failures. Updated ${completedSyncs}/${totalSyncs} AWS accounts.`, type: 'warning' });
      }
      
    } catch (error: any) {
      console.error('Sync error:', error);
      const errorMessage = error.response?.data?.error || 'Failed to sync AWS resources';
      setSyncProgress('');
      setToast({ message: errorMessage, type: 'error' });
    } finally {
      setIsSyncing(false);
      // Clear progress after 3 seconds
      setTimeout(() => setSyncProgress(''), 3000);
    }
  };

  const calculateTotalResources = () => {
    if (!summary) return { totalVcpus: 0, totalMemory: 0, totalStorage: 0 };
    
    // Include EC2 instances, EKS nodes, and RDS instances in the totals
    const totalVcpus = (summary.ec2.totalVcpus || 0) + (summary.eks.totalVcpus || 0) + (summary.rds?.totalVcpus || 0);
    const totalMemory = (summary.ec2.totalMemory || 0) + (summary.eks.totalMemory || 0) + (summary.rds?.totalMemory || 0);
    const totalStorage = (summary.ebs.totalGiB || 0) + 
                        (summary.s3.totalGiB || 0) + 
                        (summary.rds?.totalStorageGB || 0) + 
                        (summary.fsx?.totalStorageGB || 0) + 
                        (summary.efs?.totalGiB || 0);
    
    return {
      totalVcpus,
      totalMemory,
      totalStorage
    };
  };

  const calculateTotalMonthlyCost = () => {
    if (!summary) return 0;
    return (summary.ec2.monthlyComputeCost || 0) + 
           (summary.ebs.monthlyCost || 0) + 
           (summary.s3.monthlyCost || 0) + 
           (summary.eks.monthlyComputeCost || 0) + 
           (summary.rds?.totalMonthlyCost || 0) +
           (summary.fsx?.monthlyCost || 0) +
           (summary.efs?.monthlyCost || 0) +
           (summary.dynamodb?.monthlyCost || 0) +
           (summary.cloudwatchLogs?.monthlyCost || 0) +
           (summary.cloudfront?.monthlyCost || 0) +
           (summary.ecs?.monthlyCost || 0);
  };

  const hasAnyResources = () => {
    if (!summary) return false;
    
    return (
      summary.ec2.instances > 0 ||
      summary.ebs.volumes > 0 ||
      summary.s3.buckets > 0 ||
      summary.eks.clusters > 0 ||
      (summary.rds?.instances || 0) > 0 ||
      (summary.fsx?.fileSystems || 0) > 0 ||
      (summary.efs?.fileSystems || 0) > 0 ||
      (summary.dynamodb?.tables || 0) > 0 ||
      (summary.cloudwatchLogs?.logGroups || 0) > 0 ||
      (summary.cloudfront?.distributions || 0) > 0 ||
      (summary.ecs?.clusters || 0) > 0
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const totalResources = calculateTotalResources();
  const totalMonthlyCost = calculateTotalMonthlyCost();

  // Calculate actual values for debug panel
  const actualDebugValues: DebugValues = {
    ec2Vcpus: summary?.ec2.totalVcpus || 0,
    ec2Memory: summary?.ec2.totalMemory || 0,
    s3Storage: summary?.s3.totalGiB || 0,
    monthlySpend: totalMonthlyCost,
    eksVcpus: summary?.eks.totalVcpus || 0,
    eksMemory: summary?.eks.totalMemory || 0
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Debug Panel - Only show in development */}
      {process.env.NODE_ENV === 'development' && summary && (
        <DebugPanel
          actualValues={actualDebugValues}
          onValuesChange={setDebugValues}
          isOpen={debugPanelOpen}
          onToggle={() => setDebugPanelOpen(!debugPanelOpen)}
        />
      )}
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <Logo size="md" />
            
            <div className="flex items-center space-x-4">
              {/* User Info */}
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {user?.firstName && user?.lastName 
                    ? `${user.firstName} ${user.lastName}`
                    : user?.email?.split('@')[0]}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {user?.companyName || 'No company'}
                </p>
              </div>
              
              {/* Logout Button */}
              <button
                onClick={logout}
                className="flex items-center space-x-2 px-3 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors duration-150"
                title="Logout"
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>


      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-3 sm:px-6 lg:px-8">
        <div className="px-4 py-3 sm:px-0">
          {error && (
            <div className="mb-6 rounded-md bg-red-50 dark:bg-red-900/50 p-4">
              <div className="flex">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                    Error loading data
                  </h3>
                  <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                    {error}
                  </div>
                </div>
              </div>
            </div>
          )}

          {summary && hasAnyResources() ? (
            <>
              {/* Action Buttons with Sync Status and Title */}
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-medium text-gray-900 dark:text-white">
                  Resource Overview
                </h2>
                
                {/* Sync Status Display */}
                {(isSyncing || syncProgress) && (
                  <div className="flex items-center">
                    <div className="bg-indigo-900 text-white px-4 py-2 rounded-lg flex items-center space-x-3 shadow-md">
                      {isSyncing ? (
                        <>
                          <CloudArrowDownIcon className="h-5 w-5 animate-pulse" />
                          <div className="flex items-center space-x-2">
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                            <span className="text-sm font-medium">
                              {syncProgress || 'Gathering AWS resources...'}
                            </span>
                          </div>
                        </>
                      ) : (
                        <>
                          <svg className="h-5 w-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-sm font-medium text-green-400">
                            {syncProgress}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                )}
                {syncError && (
                  <div className="flex items-center">
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg px-4 py-2 flex items-center space-x-2">
                      <ExclamationTriangleIcon className="h-4 w-4 text-red-500" />
                      <span className="text-sm text-red-700 dark:text-red-300">
                        {syncError}
                      </span>
                      <button
                        onClick={() => setSyncError('')}
                        className="ml-2 text-xs text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Action Buttons */}
                <div className="flex space-x-3 ml-auto">
                <button
                  onClick={() => setShowSyncPanel(true)}
                  className="inline-flex items-center px-4 py-2 border border-indigo-800 text-sm font-medium rounded-md text-white bg-indigo-950 hover:bg-indigo-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                  </svg>
                  AWS IAM Keys
                </button>
                
                <button
                  onClick={() => handleSyncAllRegions(true)}
                  disabled={isSyncing || isRefreshing}
                  className="inline-flex items-center px-4 py-2 border border-indigo-800 text-sm font-medium rounded-md text-white bg-indigo-950 hover:bg-indigo-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Unified sync - resources and bandwidth"
                >
                  <ArrowPathIcon className={`h-4 w-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`} />
                  {isSyncing ? 'Syncing...' : 'Re-Sync'}
                </button>
                
                <button
                  onClick={handleExportExcel}
                  className="inline-flex items-center px-4 py-2 border border-indigo-800 text-sm font-medium rounded-md text-white bg-indigo-950 hover:bg-indigo-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                  Export Excel
                </button>
                </div>
              </div>

              {/* Resource Overview Cards */}
              <div className="mb-8">
                <ResourceSummaryCards 
                  summary={summary} 
                  totalResources={totalResources}
                  totalMonthlyCost={totalMonthlyCost}
                  billingData={billingData}
                />
              </div>

              {/* Service Details */}
              <ServiceDetailsView />

              {/* Resource Distribution Charts */}
              <div className="mb-8">
                <ResourcePieCharts summary={summary} />
              </div>

              {/* Bandwidth Dashboard */}
              <div className="mb-8">
                <BandwidthDashboard onDataLoad={(gb, cost) => {
                  setBandwidthTotalGB(gb);
                  setBandwidthCost(cost);
                }} />
              </div>

              {/* AWS Repatriation Budget/Plan - only show for public website users */}
              {showRepatriationPlan && (
                <div className="mt-8">
                  <AWSRepatriationPlan 
                    summary={summary}
                    totalVcpus={debugValues ? (debugValues.ec2Vcpus + debugValues.eksVcpus) : totalResources.totalVcpus}
                    totalMemory={debugValues ? (debugValues.ec2Memory + debugValues.eksMemory) : totalResources.totalMemory}
                    totalStorage={debugValues?.s3Storage ?? ((summary.ebs.totalGiB || 0) + (summary.rds?.totalStorageGB || 0))}
                    totalMonthlyCost={debugValues?.monthlySpend ?? totalMonthlyCost}
                    bandwidthTotalGB={bandwidthTotalGB}
                    bandwidthCost={bandwidthCost}
                  />
                </div>
              )}
            </>
          ) : (
            <div className="max-w-4xl mx-auto">
              <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg overflow-hidden">
                <div className="p-8 text-center">
                  <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                    Welcome
                  </h2>
                  <p className="text-lg text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
                    Let's analyze your AWS infrastructure and determine if exiting the cloud could save you money.
                  </p>
                  
                  <div className="bg-slate-900 rounded-lg p-6 mb-8">
                    <h3 className="text-lg font-semibold text-white mb-4">
                      Get Started in 3 Easy Steps
                    </h3>
                    <div className="space-y-4 text-left max-w-xl mx-auto">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <div className="w-8 h-8 bg-indigo-900 rounded-full flex items-center justify-center">
                            <span className="text-sm font-bold text-indigo-300">1</span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <h4 className="text-sm font-medium text-white">Create AWS API Credentials</h4>
                          <p className="text-sm text-gray-300">Generate read-only access keys in your AWS console</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <div className="w-8 h-8 bg-indigo-900 rounded-full flex items-center justify-center">
                            <span className="text-sm font-bold text-indigo-300">2</span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <h4 className="text-sm font-medium text-white">Add Credentials to E[X]ITAWS</h4>
                          <p className="text-sm text-gray-300">Securely connect your AWS account</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <div className="w-8 h-8 bg-indigo-900 rounded-full flex items-center justify-center">
                            <span className="text-sm font-bold text-indigo-300">3</span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <h4 className="text-sm font-medium text-white">Sync & Analyze</h4>
                          <p className="text-sm text-gray-300">Review your infrastructure costs and migration options</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => setShowSyncPanel(true)}
                    className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 shadow-lg"
                  >
                    <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                    </svg>
                    Add AWS Credentials
                  </button>
                  
                  <p className="mt-6 text-sm text-gray-500 dark:text-gray-400">
                    Need help? Check out our{' '}
                    <a href="/documentation" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-500 dark:text-indigo-400">
                      documentation
                    </a>{' '}
                    for detailed setup instructions.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Sync Panel Modal */}
      {showSyncPanel && (
        <SyncPanel
          isOpen={showSyncPanel}
          onClose={() => setShowSyncPanel(false)}
          onSyncComplete={loadDashboardData}
        />
      )}
      
      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default Dashboard;